import { type CSSResultGroup, html, LitElement, nothing, type PropertyValues, unsafeCSS } from 'lit'
import { customElement, property, query, queryAssignedElements, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { ContextRoot, provide } from '@lit/context'
import { debounce, emit } from '@/navbar/utils'
import { isArrowDown, isArrowUp, isShift, isTab } from '@/navbar/utils/eventKeys'
import { logError, logWarn, setErrorReporter, setLogLevel, type ErrorReporter, type LogLevel } from '@/utils/errorReporter'
import { DEFAULT_LOGOMARK_SRC } from '@/navbar/constants'
import { navbarContext, type NavbarPart } from './context'
import { $t, setLocale } from '@/navbar/i18n'
import { NAMESPACE_LABELS } from '@/navbar/constants/namespace-labels'
import { subscribe, unsubscribe } from '@/i18n/subscribers'
import fallbackMarketingData from '../data/fallbackMarketingData'

// APIs:
import MarketingDataApi from '../api/marketingData.api'
import IdentityApi from '../api/identity.api'
import AssistApi from '../api/assist.api'

// Types/local class imports:
import { type EcoSuiteNavbarMobileMenu } from './mobile-menu/eco-suite-navbar-mobile-menu.component'
import { type ResolvedTarget } from './skip-to-content/eco-suite-navbar-skip-to-content.component'
import type { EcoSuiteNavbarAppSwitcher } from './app-switcher/eco-suite-navbar-app-switcher.component'
import type { EcoSuiteNavbarButton } from './button/eco-suite-navbar-button.component'
import type { MenuItem, UserProduct, SkipToContentTarget, EcoAssist, NavbarAppSwitcherMarketingData } from '@/navbar/navbar.types'

// Styles:
import styles from './eco-suite-navbar.scss?inline'

// Child components:
import './app-switcher/eco-suite-navbar-app-switcher.component'
import './tooltip/eco-suite-navbar-tooltip.component'
import './help/eco-suite-navbar-help.component'
import './account-menu/eco-suite-navbar-account-menu.component'
import './button/eco-suite-navbar-button.component'
import './mobile-menu/eco-suite-navbar-mobile-menu.component'
import './skip-to-content/eco-suite-navbar-skip-to-content.component'
import './side-menu-button/eco-suite-navbar-side-menu-button.component'

// Prism imports:
import '../prism/components'
import '../prism/icons'


/**
 * @summary The main navbar component. Handles authentication, app switching, help, account menus, and responsive layout.
 * @tag eco-suite-navbar
 * @slot home-suffix - Content rendered immediately after the logo/home link.
 * @slot search - Search input or control rendered in the navbar toolbar. Be sure to give your search slot content `width: 100%` so it behaves as expected.
 * @slot utilities - Additional utility controls rendered in the right section of the navbar.
 * @slot help-menu - Content rendered inside the help menu trigger area.
 * @slot account-menu - Content rendered inside the account menu trigger area.
 * @csspart side-menu-toggle - The button that toggles the side menu, if `has-side-menu` is true. Useful for controlling visibility of the button via media queries.
 * @cssproperty [--eco-suite-navbar-z-index=1] - Controls the stacking order of the entire navbar.
 * @event eco-mobile-menu-opened - Fired when the mobile menu is opened.
 * @event eco-mobile-menu-closed - Fired when the mobile menu is closed.
 * @event eco-assist-opened - Fired when the Assist panel is opened.
 * @event eco-assist-closed - Fired when the Assist panel is closed.
 * @event eco-assist-minimised - Fired when the Assist panel is minimised.
 * @event eco-search-expanded - Fired when the search bar is expanded (on mobile/tablet).
 * @event eco-search-collapsed - Fired when the search bar is collapsed (on mobile/tablet).
 */
@customElement('eco-suite-navbar')
export class EcoSuiteNavbar extends LitElement {
  /** @internal */ static readonly tagName = 'eco-suite-navbar'
  /** @internal */ static readonly styles: CSSResultGroup = [unsafeCSS(styles)]
  /** @internal */ public static readonly DEFAULT_LOCALE = 'en'
  /** @internal */ private resizeObserver?: ResizeObserver
  /** @internal */ private searchCloseWatcher?: CloseWatcher | null
  /** @internal */ private _clickListener?: (e: MouseEvent) => void

  /** @internal */ private readonly _onAssistOpen = () => {
    this.mobileMenu?.close?.()
    emit(this, 'eco-assist-opened')
  }
  /** @internal */ private readonly _onAssistClose = () => emit(this, 'eco-assist-closed')
  /** @internal */ private readonly _onAssistMinimise = () => emit(this, 'eco-assist-minimised')

  /** @internal */
  @queryAssignedElements({ slot: 'search' })
  private readonly searchSlotElements?: HTMLElement[]

  /** @internal */
  @query('.EcoSuiteNavbar__mobileToggle')
  private readonly mobileMenuToggleButton?: EcoSuiteNavbarButton

  /** @internal */
  @query('eco-suite-navbar-mobile-menu')
  private readonly mobileMenu?: EcoSuiteNavbarMobileMenu

  /** @internal */
  @query('.EcoSuiteNavbar__searchBarToggle')
  private readonly searchToggleButton?: HTMLElement

  /** @internal */
  @query('eco-suite-navbar-app-switcher')
  private readonly appSwitcher?: EcoSuiteNavbarAppSwitcher

  /** Callback returning a valid EcoID bearer token (required if not passing the user's info manually). */
  @property({ attribute: false })
  getToken?: () => Promise<string>

  /** Domain for EcoID calls (if `getToken` is provided). */
  @property({ attribute: 'eco-id-domain' })
  ecoIdDomain?: string

  /** Domain for Assist calls. */
  @property({ attribute: 'assist-domain' })
  assistDomain?: string

  /**
   * Platform URL for the All Apps link in the App Switcher. EcoID users only (not required if `getToken` is provided).
   * `links.homepage` property from the `userdata` endpoint can be used in place of an environment variable.
   * Falls back to a marketing site URL if empty.
   * */
  @property({ attribute: 'platform-url' })
  platformUrl?: string

  /** Identifier of the current product. */
  @property({ attribute: 'current-product' })
  currentProduct?: string

  /** User's locale in RFC-5646 format */
  @property()
  locale: string = EcoSuiteNavbar.DEFAULT_LOCALE

  /** User's region. */
  @property()
  region?: string

  /** Where to link to from the home anchor. */
  @property({ attribute: 'home-href' })
  homeHref = globalThis.location.origin

  /** Callback for when the home anchor is clicked. Receives the click event; call `preventDefault()` to prevent navigation. */
  @property({ attribute: false })
  onHomeClick?: (e: Event) => void

  /** Whether the user is a guest. */
  @property({ type: Boolean, attribute: 'is-guest' })
  isGuest = false

  /** Whether to hide the Account Menu. */
  @property({ type: Boolean, attribute: 'hide-account-menu' })
  hideAccountMenu = false

  /** Items to display in the Account Menu. */
  @property({ type: Array, attribute: 'account-menu-items' })
  accountMenuItems?: MenuItem[]

  /** Callback to execute when the Logout item is clicked. Receives the click event; call `preventDefault()` to prevent navigation. */
  @property({ attribute: false })
  onLogout?: (e: Event) => void

  /** URL to navigate to when the Logout item is clicked */
  @property({ attribute: 'logout-href' })
  logoutHref?: string

  /** Items to display in the Help Menu. */
  @property({ type: Array, attribute: 'help-menu-items' })
  helpMenuItems?: MenuItem[]

  /** Where to link to from the Help button or Help menu item. */
  @property({ attribute: 'help-href' })
  helpHref?: string

  /** Callback to execute when the Help button or Help menu item is clicked. Receives the click event; call `preventDefault()` to prevent navigation. */
  @property({ attribute: false })
  onHelpClick?: (e: Event) => void

  /** Custom logo URL if white-labelling is enabled for the user's organisation (not required if `getToken` is provided). */
  @property({ attribute: 'logo-src' })
  logoSrc = DEFAULT_LOGOMARK_SRC

  /** User's full name (not required if `getToken` is provided). */
  @property({ attribute: 'user-name' })
  userName?: string

  /** User's list of products from the EcoID userdata endpoint (not required if `getToken` is provided). */
  @property({ type: Array, attribute: 'user-products' })
  userProducts: UserProduct[] = []

  /** User's organisation ID (not required if `getToken` is provided). */
  @property({ attribute: 'organisation-id' })
  organisationId?: string

  /** User's organisation name (not required if `getToken` is provided). */
  @property({ attribute: 'organisation-name' })
  organisationName?: string

  /** User's tenant ID (not required if organisation ID is provided). */
  @property({ attribute: 'tenant-id' })
  tenantId?: string

  /** Skip-to-content targets. When not provided, defaults to the first focusable element in the document. */
  @property({ attribute: false })
  skipToContent?: SkipToContentTarget | SkipToContentTarget[]

  /** Optional error reporter for forwarding errors to an external monitoring service (e.g. Datadog RUM). */
  @property({ attribute: false })
  set errorReporter(reporter: ErrorReporter | undefined) {
    this._errorReporter = reporter
    setErrorReporter(reporter)
  }
  get errorReporter(): ErrorReporter | undefined {
    return this._errorReporter
  }
  /** @internal */
  private _errorReporter?: ErrorReporter

  /** Minimum severity level for log output (default: 'error'). */
  @property({ attribute: 'log-level' })
  set logLevel(level: LogLevel) {
    this._logLevel = level
    setLogLevel(level)
  }
  get logLevel(): LogLevel {
    return this._logLevel
  }
  /** @internal */
  private _logLevel: LogLevel = 'error'

  /** Whether to disable the Assist button, e.g. in Offline mode. */
  @property({ type: Boolean, attribute: 'disable-assist' })
  disableAssist = false

  /**
   * Whether the application has a side menu. If `true`, a toggle button will be displayed.
   * You can further configure the display of this button (e.g. using media queries) via the
   * `side-menu-toggle` CSS part.
   * Note that the navbar does not implement the side menu itself; you will need
   * to implement that separately and toggle its visibility in the callback provided to `onSideMenuToggle`.
   * */
  @property({ type: Boolean, attribute: 'has-side-menu' })
  hasSideMenu = false

  /** Whether the side menu is open. */
  @property({ type: Boolean, attribute: 'side-menu-open' })
  sideMenuOpen = false

  /** Callback to execute when the side menu toggle button is clicked. */
  @property({ attribute: false })
  onSideMenuToggle?: () => void

  /** @internal */
  @property({ type: Boolean, attribute: 'is-static' })
  isStatic = false

  /** @internal */
  @provide({ context: navbarContext })
  @state()
  private navbarContext?: NavbarPart

  /** @internal */
  @state()
  private ready = false

  /** @internal */
  @state()
  private ecoAssist: EcoAssist | undefined

  /** @internal */
  @state()
  private searchExpanded = false

  /** @internal */
  @state()
  private showSearchBar = false

  /** @internal */
  @state()
  private mobileMenuOpen = false

  /** @internal */
  @state()
  private marketingData?: NavbarAppSwitcherMarketingData

  /** @internal */
  @state()
  private fetchedUserData = false

  /** @internal */
  private get hasSearch() {
    return !!this.searchSlotElements?.length
  }

  /** @internal */
  private get isDefaultLogo() {
    return this.logoSrc === DEFAULT_LOGOMARK_SRC
  }

  /** @internal */
  get resolvedSkipTargets(): ResolvedTarget[] {
    if (this.skipToContent) {
      const items = Array.isArray(this.skipToContent) ? this.skipToContent : [this.skipToContent]
      return items.map((item, idx) => ({
        label: item.label ?? (idx === 0 ? $t('navbar.mainContent') : `${idx + 1}`),
        target: item.target,
      }))
    }

    const firstFocusableEl = <HTMLElement | null>document.querySelector('a, button, input')
    if (firstFocusableEl) {
      return [{ label: $t('navbar.mainContent'), target: firstFocusableEl }]
    }
    return []
  }

  /** @internal */
  private get currentProductName() {
    if (!this.currentProduct) return 'EcoOnline'

    const data = this.marketingData ?? fallbackMarketingData
    if (data) {
      const ns = data.namespaces.find(n => n.products.includes(this.currentProduct!))
      if (ns) return NAMESPACE_LABELS[ns.identifier] ?? ns.identifier
    }
    return NAMESPACE_LABELS[this.currentProduct] ?? this.currentProduct
  }

  constructor() {
    super()
    const contextRoot = new ContextRoot()
    contextRoot.attach(this)
  }

  async connectedCallback() {
    super.connectedCallback()
    subscribe(this)

    const debouncedSetDeviceSize = debounce(() => this.setDeviceSize(), 300)
    this.resizeObserver = new ResizeObserver(() => debouncedSetDeviceSize())
    this.resizeObserver.observe(document.body)

    this.setDeviceSize()

    if (!this.currentProduct) {
      logWarn(new Error('No currentProduct provided. Some functionality will not work as expected.'), { source: 'EcoSuiteNavbar.connectedCallback' })
    }

    await this.fetchMarketingData()
    this.ready = true
  }

  protected willUpdate(changedProperties: PropertyValues): void {
    const c = (property: keyof this) => changedProperties.has(property)

    const setEcoAssistOrgId = (id?: string) => {
      if (id && this.ecoAssist && !this.ecoAssist.organisationId) {
        this.ecoAssist.organisationId = id
      }
    }

    if (c('assistDomain')) void this.loadAssist()
    if (c('ecoIdDomain') || c('getToken')) void this.fetchUserData()
    if (c('locale')) void setLocale(this.locale)
    if (c('organisationId') || c('tenantId')) setEcoAssistOrgId(this.organisationId ?? this.tenantId)
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()
    setErrorReporter(undefined)
    unsubscribe(this)
    this.resizeObserver?.disconnect()
    this.removeAssistEventListeners()
  }

  /** @internal */
  private async fetchUserData() {
    if (this.isGuest || this.fetchedUserData) return

    if (!this.ecoIdDomain || !this.getToken || !(await this.getToken())) {
      let missingData = [
        !this.ecoIdDomain && 'ecoIdDomain',
        !this.getToken && 'getToken',
      ].filter(Boolean).join(', ')

      // Don't want to await getToken() again or store the result in memory, so just assume it's returning empty in this case
      if (!missingData.length) missingData = 'getToken (returning a valid EcoID token)'

      logWarn(new Error(`Missing required props for fetching user data: ${missingData}.`), { source: 'EcoSuiteNavbar.fetchUserData' })
      return
    }

    try {
      const identityApi = new IdentityApi(this.ecoIdDomain, this.getToken)
      const user = await identityApi.fetchUser()
      if (!user) throw new Error('Issue fetching user data')

      const { userInfo, userData } = user
      this.userName = userInfo?.name
      this.organisationId = userInfo?.sub
      this.organisationName = userInfo?.organisation_name
      this.userProducts = userData?.data?.applications ?? []
      const logoSrc = userInfo?.organisation_logo_url
      this.logoSrc = logoSrc?.length ? logoSrc : DEFAULT_LOGOMARK_SRC
      this.platformUrl = userData?.data?.links?.homepage
      this.fetchedUserData = true
    } catch (e) {
      logError(e, { source: 'EcoSuiteNavbar.fetchUserData' })
    }
  }

  /** @internal */
  private async fetchMarketingData() {
    const marketingDataApi = new MarketingDataApi()
    this.marketingData = await marketingDataApi.fetchMarketingData()
  }

  /** @internal */
  private async loadAssist() {
    if (this.isGuest || this.ecoAssist) return

    if (!(this.organisationId || this.tenantId) || !this.assistDomain) {
      const missingData = [
        !this.assistDomain && 'assistDomain',
        !this.organisationId && !this.tenantId && 'organisationId or tenantId',
      ].filter(Boolean).join(', ')

      logWarn(new Error(`Missing required props for loading Assist: ${missingData}`), { source: 'EcoSuiteNavbar.loadAssist' })

      /*
        If we at least have the assistDomain we can still load the Assist script.
        A "no access" message will display in the panel if tenantId and organisationId are missing.
        Without an Assist domain we can't even load the Assist script so return early.
      */
      if (!this.assistDomain) return
    }

    const assistApi = new AssistApi(this.assistDomain, this.organisationId, this.tenantId)
    assistApi.addScriptTag()
    await customElements.whenDefined('eco-assist')
    this.ecoAssist = document.querySelector('eco-assist') ?? undefined

    if (this.ecoAssist) {
      this.ecoAssist.product = this.currentProduct
      this.ecoAssist.organisationId = this.organisationId ?? this.tenantId
      this.addAssistEventListeners()
    }
  }

  /** @internal */
  private addAssistEventListeners() {
    if (!this.ecoAssist) return

    this.ecoAssist.addEventListener('open', this._onAssistOpen)
    this.ecoAssist.addEventListener('close', this._onAssistClose)
    this.ecoAssist.addEventListener('minimise', this._onAssistMinimise)
  }

  /** @internal */
  private removeAssistEventListeners() {
    if (!this.ecoAssist) return

    this.ecoAssist.removeEventListener('open', this._onAssistOpen)
    this.ecoAssist.removeEventListener('close', this._onAssistClose)
    this.ecoAssist.removeEventListener('minimise', this._onAssistMinimise)
  }

  /** @internal */
  private setDeviceSize() {
    // Breakpoint values from the prism-design-tokens package. A Rolldown issue prevents us from importing the JSON tokens from a peer dependency with the correct import attributes so we're hardcoding them here for now.
    const smScreen = 480
    const mdScreen = 768
    const navbarContext = globalThis.innerWidth < smScreen ? 'mobile' : 'desktop'
    const showSearchBar = globalThis.innerWidth < mdScreen
    if (this.navbarContext === navbarContext && this.showSearchBar === showSearchBar) return

    this.navbarContext = navbarContext
    this.showSearchBar = showSearchBar
  }

  /** @internal */
  private toggleSearch(force?: boolean, autoFocus = false) {
    this.searchExpanded = force ?? !this.searchExpanded
    if (autoFocus) this.searchToggleButton?.focus()

    if (this.searchExpanded) {
      this._clickListener = (e: MouseEvent) => {
        const target = e.target as HTMLElement
        const searchSlot = this.shadowRoot?.querySelector('slot[name="search"]') as HTMLSlotElement
        const isSearchSlot = searchSlot?.assignedElements().includes(target)
        if (!isSearchSlot && this.searchExpanded) this.toggleSearch(false)
      }
      document.addEventListener('click', this._clickListener)

      if ('CloseWatcher' in globalThis) {
        this.searchCloseWatcher?.destroy()
        this.searchCloseWatcher = new CloseWatcher()
        this.searchCloseWatcher.onclose = () => this.toggleSearch(false, true)
      }

      if (this.showSearchBar) emit(this, 'eco-search-expanded')
    } else {
      this.searchCloseWatcher?.destroy()
      if (this._clickListener) {
        document.removeEventListener('click', this._clickListener)
        this._clickListener = undefined
      }
      if (this.showSearchBar) emit(this, 'eco-search-collapsed')
    }
  }

  /** @internal */
  private handleSearchKeydown(e: KeyboardEvent) {
    if (this.showSearchBar) {
      if (isArrowUp(e) || isShift(e)) {
        this.toggleSearch(false, true)
      }
      return
    }
    const closing = isTab(e)
    this.toggleSearch(!closing, closing)
  }

  /** @internal */
  private handleSearchKeyup(e: KeyboardEvent) {
    if (!this.showSearchBar && isTab(e)) {
      this.toggleSearch(true)
    }
  }

  /** @internal */
  private async handleSearchIconKeydown(e: KeyboardEvent) {
    if (isArrowDown(e)) {
      if (!this.searchExpanded) {
        this.toggleSearch(true)
        await this.updateComplete
      }
      this.searchSlotElements?.[0]?.focus()
    }
  }

  /** @internal */
  private toggleMobileMenu(e: MouseEvent) {
    e.stopPropagation()
    this.mobileMenu?.toggle?.()
  }

  render() {
    return html`
      <nav
        class=${classMap({
          EcoSuiteNavbar: true,
          'EcoSuiteNavbar--static': !!this.isStatic,
          'EcoSuiteNavbar--searchBarExpanded': this.showSearchBar && this.searchExpanded,
        })}
      >
        ${
          this.resolvedSkipTargets.length ? html`
            <eco-suite-navbar-skip-to-content
              .targets=${this.resolvedSkipTargets}
              @eco-dismissed=${() => this.appSwitcher?.focus()}
            ></eco-suite-navbar-skip-to-content>
          ` : nothing
        }

        <div class="EcoSuiteNavbar__row">
          <div class="EcoSuiteNavbar__left">
            ${
              this.hasSideMenu ? html`
                <eco-suite-navbar-side-menu-button
                  part="side-menu-toggle"
                  .onToggle=${this.onSideMenuToggle}
                  .isActive=${this.sideMenuOpen}
                  data-qa-id="side-menu-button"
                >
              ` : nothing
            }

            ${
              this.isGuest ? nothing : (this.ready
                ? html`
                  <eco-suite-navbar-app-switcher
                    .userProducts=${this.userProducts}
                    .currentProduct=${this.currentProduct}
                    .homeHref=${this.homeHref}
                    .marketingData=${this.marketingData}
                    .platformUrl=${this.platformUrl}
                  ></eco-suite-navbar-app-switcher>`
                : html`
                  <eco-suite-navbar-button
                    icon="app-switcher"
                    label=${$t('appSwitcher.switchProduct')}
                    .forceNavbarContext=${'desktop'}
                    disabled
                  ></eco-suite-navbar-button>`
              )
            }

            <eco-suite-navbar-tooltip content=${$t('navbar.home')} class="EcoSuiteNavbar__homeTooltip">
              <a
                class=${classMap({
                  EcoSuiteNavbar__homeLink: true,
                  'EcoSuiteNavbar__homeLink--defaultLogo': this.isDefaultLogo,
                })}
                href=${this.homeHref}
                data-qa-id="home-link"
                @click=${this.onHomeClick}
              >
                <img
                  src=${this.logoSrc}
                  alt=${this.isDefaultLogo ? 'EcoOnline' : (this.organisationName ?? 'EcoOnline')}
                />
                ${this.ready ? html`<span>${this.currentProductName}</span>` : nothing}
              </a>
            </eco-suite-navbar-tooltip>
            <slot name="home-suffix"></slot>
          </div>

          <div class="EcoSuiteNavbar__right">
            ${
              this.showSearchBar ? nothing : html`
                <div class=${classMap({
                  EcoSuiteNavbar__search: true,
                  'EcoSuiteNavbar__search--hasSearch': this.hasSearch,
                  'EcoSuiteNavbar__search--expanded': this.searchExpanded,
                })}>
                  <slot
                    name="search"
                    @keydown=${this.handleSearchKeydown}
                    @keyup=${this.handleSearchKeyup}
                    @click=${() => this.toggleSearch(true)}
                  ></slot>
                </div>
              `
            }

            ${
              this.hasSearch ? html`
                <eco-suite-navbar-button
                  class="EcoSuiteNavbar__searchBarToggle"
                  icon="search"
                  .isActive=${this.searchExpanded}
                  .forceNavbarContext=${'desktop'}
                  aria-controls="search-bar"
                  aria-expanded=${this.searchExpanded}
                  tooltip=${$t('navbar.search')}
                  aria-label=${$t('navbar.search')}
                  data-qa-id="search-toggle-button"
                  @click=${(e: MouseEvent) => {
                    e.stopImmediatePropagation()
                    this.toggleSearch()
                  }}
                  @keydown=${this.handleSearchIconKeydown}
                ></eco-suite-navbar-button>
              ` : nothing
            }

            ${
              this.navbarContext === 'mobile' ? nothing : html`<slot class="hideOnMobile" name="utilities"></slot>`
            }

            ${
              this.navbarContext === 'mobile' ? nothing : html`
                <eco-suite-navbar-help
                  .menuItems=${this.helpMenuItems}
                  .href=${this.helpHref}
                  .onHelpClick=${this.onHelpClick}
                  class="hideOnMobile"
                >
                  <slot name="help-menu"></slot>
                </eco-suite-navbar-help>
              `
            }

            ${
              (this.isGuest || this.navbarContext === 'mobile') ? nothing
                : (this.disableAssist || !this.ecoAssist)
                  ? html`
                    <eco-suite-navbar-button
                      label="Assist"
                      icon="eco-ai"
                      class="hideOnMobile"
                      disabled
                    ></eco-suite-navbar-button>`
                  : html`
                    <eco-suite-navbar-tooltip
                      content="Assist"
                      class="hideOnMobile"
                      data-qa-id="assist-button-tooltip"
                    >
                      <eco-assist-trigger-icon
                        .ecoAssist=${this.ecoAssist}
                        data-qa-id="assist-button"
                      ></eco-assist-trigger-icon>
                    </eco-suite-navbar-tooltip>`
            }

            ${
              (this.hideAccountMenu || this.navbarContext === 'mobile') ? nothing : html`
                <eco-suite-navbar-account-menu
                  .isGuest=${this.isGuest}
                  .userName=${this.userName}
                  .menuItems=${this.accountMenuItems}
                  .onLogout=${this.onLogout}
                  .logoutHref=${this.logoutHref}
                  class="hideOnMobile"
                >
                  <slot name="account-menu"></slot>
                </eco-suite-navbar-account-menu>
              `
            }

            ${
              this.navbarContext === 'desktop' ? nothing : html`
                <eco-suite-navbar-button
                  class="EcoSuiteNavbar__mobileToggle"
                  .isActive=${this.mobileMenuOpen}
                  icon="three-dots-vertical"
                  .forceNavbarContext=${'desktop'}
                  aria-controls="mobile-menu"
                  aria-expanded=${this.mobileMenuOpen}
                  tooltip=${$t('navbar.menu')}
                  aria-label=${$t('navbar.menu')}
                  data-qa-id="mobile-menu-button"
                  @click=${this.toggleMobileMenu}
                  @mousedown=${(e: MouseEvent) => {
                    if (this.mobileMenuOpen) e.stopImmediatePropagation()
                  }}
                ></eco-suite-navbar-button>
              `
            }
          </div>
        </div>

        <div
          id="search-bar"
          class=${classMap({
            'EcoSuiteNavbar__searchBar': true,
            'EcoSuiteNavbar__searchBar--expanded': this.searchExpanded,
          })}
          ?inert=${!this.searchExpanded}
        >
          ${this.showSearchBar ? html`
            <slot
              name="search"
              @keydown=${this.handleSearchKeydown}
              @keyup=${this.handleSearchKeyup}
              @click=${() => this.toggleSearch(true)}
            ></slot>
          ` : nothing}
          <pwc-icon-button
            icon="cross"
            class="EcoSuiteNavbar__searchBarClose"
            appearance="neutral"
            variant="transparent"
            size="sm"
            aria-label=${$t('navbar.closeSearch')}
            @click=${() => this.toggleSearch(false, true)}
          ></pwc-icon-button>
        </div>

        ${
          this.navbarContext === 'desktop' ? nothing : html`
            <eco-suite-navbar-mobile-menu
              id="mobile-menu"
              .userName=${this.userName}
              .ecoAssist=${this.ecoAssist}
              .disableAssist=${this.disableAssist}
              .accountMenuItems=${this.accountMenuItems}
              .onLogout=${this.onLogout}
              .logoutHref=${this.logoutHref}
              .helpMenuItems=${this.helpMenuItems}
              .helpHref=${this.helpHref}
              .onHelpClick=${this.onHelpClick}
              .isGuest=${this.isGuest}
              .hideAccountMenu=${this.hideAccountMenu}
              ?inert=${!this.mobileMenuOpen}
              @eco-mobile-menu-opened=${() => {
                this.mobileMenuOpen = true
                this.searchExpanded = false
              }}
              @eco-mobile-menu-closed=${() => {
                this.mobileMenuOpen = false
                this.mobileMenuToggleButton?.focus()
              }}
            >
              <slot slot="account-menu" name="account-menu"></slot>
              <slot slot="utilities" name="utilities"></slot>
              <slot slot="help-menu" name="help-menu"></slot>
            </eco-suite-navbar-mobile-menu>
          `
        }
      </nav>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'eco-suite-navbar': EcoSuiteNavbar
  }
}
